import { useTranslation } from 'react-i18next'

import { Card, CardBody } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/format'
import type { ExpenseLine, LegLevelMoney, Money } from '@/types/domain'

import { RATE_CURRENCIES, type LegRateDraft } from './legRows'

/* «Плечо целиком» (S-18a) — the compact card above the trucks: who carries the
 * leg, what the leg itself is agreed at, and what is still owed on it.
 *
 * The leg rate is what pays a sea or air leg, which has no trucks to carry a
 * rate, and a convoy-level charge on a road leg. It follows S-10's rule for a
 * truck rate exactly:
 *
 *   · while the leg has NO leg-level plan, a rate writer types it here and the
 *     page's one save bar writes it in the same batch as the truck rates
 *     (`legRows.composeLegRate`);
 *   · once it has one, the card shows that plan's own figure in its own
 *     currency, read-only — it is an AD-4 row the money screen edits, and an
 *     input here would write a second;
 *   · with several (written elsewhere), it shows their served total in USD
 *     (`legLevel.agreed`), read-only.
 *
 * The remainder is `legLevel.remaining`, served and signed like the leg's
 * «Остаток» — never computed here. The carrier is the LEG's, read-only and
 * edited in «Параметры плеча»; a reader not shown agent identity (Sales)
 * sees the figures and no carrier at all — a column of «скрыто» would still
 * advertise what is withheld.
 */
export function LegWholeCard({
  carrierName,
  showIdentity,
  plans,
  legLevel,
  editable,
  rate,
  onRateChange,
  heldRate,
  invalid,
}: {
  /** The leg's carrier as the page holds it; printed only with identity. */
  carrierName: string
  showIdentity: boolean
  /** The leg's leg-level PLANNED lines (leg, no truck, not unplanned). */
  plans: readonly ExpenseLine[]
  /** The served leg-level bucket; absent before the leg exists or when the
   *  block carries none. */
  legLevel?: LegLevelMoney
  /** A rate writer on an editable page — the input exists at all. */
  editable: boolean
  rate: LegRateDraft
  onRateChange: (next: LegRateDraft) => void
  /** The rate a failed press is holding — shown read-only until it lands or
   *  is discarded, never an input whose edit the retry would not send. */
  heldRate?: Money
  /** The typed figure cannot be sent; the save bar says why. */
  invalid: boolean
}) {
  const { t } = useTranslation()
  const single = plans.length === 1 ? plans[0] : undefined

  const figure = (value: Money | undefined, note?: string) => (
    <span className="flex flex-col items-start gap-0.5">
      <span className="asa-num text-text-heading text-[14px] leading-tight whitespace-nowrap">
        {value ? formatMoney(value.amount, value.currency) : '—'}
      </span>
      {note ? <span className="text-text-muted text-[12px] leading-snug font-light">{note}</span> : null}
    </span>
  )

  const rateCell = heldRate
    ? figure(heldRate)
    : single
      ? figure(single.planned)
      : plans.length > 1
        ? figure(legLevel?.agreed, t('cases.legWhole.severalRates'))
        : editable && !rate.written
          ? (
              <span className="flex items-center gap-1.5">
                <Input
                  inputSize="sm"
                  inputMode="decimal"
                  className="asa-num w-32 text-right"
                  value={rate.rate}
                  aria-label={t('cases.legWhole.rateLabel')}
                  aria-invalid={invalid || undefined}
                  onChange={(event) => onRateChange({ ...rate, rate: event.target.value })}
                />
                <Select
                  selectSize="sm"
                  className="w-[84px]"
                  value={rate.currency}
                  onChange={(currency) => onRateChange({ ...rate, currency })}
                  options={RATE_CURRENCIES.map((code) => ({ value: code, label: code }))}
                  aria-label={t('cases.legWhole.currencyLabel')}
                />
              </span>
            )
          : figure(undefined)

  return (
    <Card>
      <CardBody className="px-5 py-4">
        <section
          aria-labelledby="leg-whole-title"
          className={cn(
            'grid items-start gap-x-6 gap-y-3',
            /* One column on a phone — the rate input and its currency need
               the width — and one row from `md`. */
            showIdentity
              ? 'grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto_minmax(8rem,auto)]'
              : 'grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(8rem,auto)]',
          )}
        >
          <h2
            id="leg-whole-title"
            className="text-text-heading text-[18px] leading-tight font-normal"
          >
            {t('cases.legWhole.title')}
          </h2>

          {showIdentity ? (
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-text-muted text-[11px] tracking-wide uppercase">
                {t('cases.legWhole.carrier')}
              </span>
              <span className="text-text-body truncate text-[14px] leading-tight">
                {carrierName.trim() || '—'}
              </span>
              <span className="text-text-muted text-[12px] leading-snug font-light">
                {t('cases.legWhole.carrierHint')}
              </span>
            </div>
          ) : null}

          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-text-muted text-[11px] tracking-wide uppercase">{t('cases.legWhole.rate')}</span>
            {rateCell}
          </div>

          <div className="flex min-w-0 flex-col items-start gap-1 md:items-end md:text-right">
            <span className="text-text-muted text-[11px] tracking-wide uppercase">
              {t('cases.legWhole.remaining')}
            </span>
            <span
              data-testid="leg-whole-remaining"
              className="asa-num text-text-heading text-[16px] leading-tight font-semibold whitespace-nowrap"
            >
              {legLevel ? formatMoney(legLevel.remaining.amount, legLevel.remaining.currency) : '—'}
            </span>
          </div>
        </section>
      </CardBody>
    </Card>
  )
}
